import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AddOnsSection.module.css';
import { AddOnsGrid } from './AddOnsGrid';
import { useApplicableAddonsApi } from './useApplicableAddonsApi';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';

/**
 * Add-ons dashboard accordion section (Punchup green zone). A read-only list of the add-ons the widget
 * will show for this show, with drag-to-reorder (optimistic, auto-saved on drop — no Save button).
 *
 * Scope: reorder + display only — parity with the classic punchup iframe add-ons section. Create / edit /
 * delete live in the downstroke venue "Products & Add-ons" page; this section only orders what is already
 * configured. Ordering is partner-scoped, so a new order applies to every show of the partner.
 */
const AddOnsSection: FC = () => {
	const { t } = useTranslation();
	const { applicableAddons, isLoading, isError, refetch, queryKey } = useApplicableAddonsApi();

	if (isLoading) {
		return (
			<div className={styles.addOnsLoading}>
				<PunchupSpinner size={24} />
			</div>
		);
	}

	// A failed load must not masquerade as an empty list (which would wrongly imply the widget shows no
	// add-ons) — surface the error with a retry instead.
	if (isError) {
		return (
			<PunchupErrorState
				title={t('dashboard-addOns-error-title', "Couldn't load add-ons")}
				description={t('dashboard-addOns-error-description', 'Something went wrong loading the add-ons for this show.')}
				onRetry={() => {
					refetch();
				}}
			/>
		);
	}

	const addons = applicableAddons ?? [];

	if (addons.length === 0) {
		return (
			<p className="punchupEmptyState">
				{t('dashboard-addOns-empty', 'No add-ons will be shown on the widget for this event')}
			</p>
		);
	}

	return <AddOnsGrid addons={addons} queryKey={queryKey} />;
};

export { AddOnsSection };
